import { DEFAULT_CURRENCY_ISO } from '@rover/constants/src/i18n/language';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { DEFAULT_LOCALE } from '@rover/constants/src/i18n/locale';
import { Logger } from '@rover/rsdk/src/modules/Logging';

type FormatOptions = {
  includeDecimal?: boolean;
  roundUp?: boolean;
  formatSymbol?: ((input: string) => string) | undefined;
  formatAmount?: ((input: string) => string) | undefined;
};

const intlNumberFormatCache = new Map<string, Intl.NumberFormat>();

export function getIntlNumberFormat(
  currencyIso = DEFAULT_CURRENCY_ISO,
  locale: Locale = DEFAULT_LOCALE
): Intl.NumberFormat {
  // Constructing an Intl.NumberFormat is expensive on React Native Android, where Intl is
  // polyfilled/ICU-backed, and price UI calls this on every render (see DEV-154232: dragging the
  // search price slider constructed four formatters per frame). The returned formatter is a pure
  // function of (locale, currencyIso) and is stateless for formatting, so instances are safely
  // reusable. The key space is bounded by supported locales x active currencies.
  const cacheKey = `${locale}|${currencyIso}`;
  const cachedFormat = intlNumberFormatCache.get(cacheKey);
  if (cachedFormat) return cachedFormat;

  const numberFormat = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyIso,
  });
  intlNumberFormatCache.set(cacheKey, numberFormat);
  return numberFormat;
}

export function formatCurrency(
  amount: string | number,
  currencyIso = DEFAULT_CURRENCY_ISO,
  locale: Locale = DEFAULT_LOCALE,
  { includeDecimal = true, formatSymbol, formatAmount }: FormatOptions = {}
): string {
  const intlNumber = getIntlNumberFormat(currencyIso, locale);
  if (!intlNumber) {
    Logger.info('formatToParts error DEV-87345', {
      error: 'error executing formatToParts',
      currencyIso: `${currencyIso}`,
      locale: `${locale}`,
    });
  }
  const parts = intlNumber.formatToParts(Number(amount));
  let formattedCurrency = '';
  let formattedAmount = '';
  let currencyIndex = 0;
  let isNegative = false;
  parts.forEach((part, index) => {
    const { type, value } = part;
    if (type === 'minusSign') isNegative = true;

    if (type === 'currency') {
      currencyIndex = index;
      formattedCurrency = value;
    } else if (includeDecimal || !['decimal', 'fraction'].includes(type)) {
      formattedAmount += value;
    }
  });

  if (typeof formatSymbol === 'function') {
    formattedCurrency = formatSymbol(formattedCurrency);
  }

  if (typeof formatAmount === 'function') {
    formattedAmount = formatAmount(formattedAmount);
  }

  if (currencyIndex === 1 && isNegative) return `-${formattedCurrency}${formattedAmount.slice(1)}`;

  if (currencyIndex > 0) {
    return formattedAmount + formattedCurrency;
  }

  return formattedCurrency + formattedAmount;
}

type Price = {
  amount: number;
  currency_iso?: string;
};

export function formatCurrencyFromPriceObj(priceObj: Price, locale: Locale): string {
  return formatCurrency(String(priceObj.amount), priceObj.currency_iso, locale);
}

type Config = {
  symbol: string;
  symbolOnLeft: boolean;
  thousandsSeparator: string;
  decimalSeparator: string;
  decimalDigits: number;
};

export type CurrencyLocalData = {
  currencyConfig: {
    symbol: string;
    decimalDigits: number;
  };
  localeConfig: {
    locale: Locale;
    symbolOnLeft: boolean;
    thousandsSeparator: string;
    decimalSeparator: string;
  };
};

const getConfigFromFormatter = (currencyIso: string, locale: Locale = DEFAULT_LOCALE): Config => {
  const formatter = getIntlNumberFormat(currencyIso, locale);
  const config: Partial<Config> = {};
  formatter.formatToParts(100000.0).forEach(({ type, value }, index) => {
    if (type === 'currency') {
      config.symbol = value;
      config.symbolOnLeft = index === 0;
    }

    if (type === 'group') {
      config.thousandsSeparator = value;
    }

    if (type === 'decimal') {
      config.decimalSeparator = value;
    }

    if (type === 'fraction') {
      config.decimalDigits = value.length;
    }
  });
  return config as Config;
};

export function getCurrencyAndLocaleConfig(
  currencyIso: string,
  locale: Locale = DEFAULT_LOCALE
): CurrencyLocalData {
  const config = getConfigFromFormatter(currencyIso, locale);
  return {
    currencyConfig: {
      symbol: config.symbol,
      decimalDigits: config.decimalDigits,
    },
    localeConfig: {
      locale,
      symbolOnLeft: config.symbolOnLeft,
      thousandsSeparator: config.thousandsSeparator,
      decimalSeparator: config.decimalSeparator,
    },
  };
}
